import axios from 'axios';
import { BASE_URL } from '../types/constants';
import {
    Subscription,
    SubscriptionCheckoutResponse,
    SubscriptionMarketCatalog,
    SubscriptionMarketCatalogApi,
    SubscriptionPlan,
    SubscriptionPlanApi
} from '../types/payment.types';
import { buildMarketingAttributionHeaders } from './marketing.service';

/**
 * Payment API helpers. All calls require a Bearer token from the caller.
 */
export const getSubscriptionDetails = async (token: string): Promise<Subscription | null> => {
    const response = await axios.get<Subscription>(`${BASE_URL}/v1/payment/subscription-details`, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
    });
    return response.data;
};

const normalizeSubscriptionPlan = (plan: SubscriptionPlanApi): SubscriptionPlan => ({
    price: Number(plan.Price),
    currency: plan.Currency?.toLowerCase(),
    minorUnitDigits: Number(plan.MinorUnitDigits),
    locale: plan.Locale,
    unitAmount: Number(plan.UnitAmount),
    recurringInterval: plan.RecurringInterval?.toLowerCase(),
    recurringIntervalCount: Number(plan.RecurringIntervalCount),
    duration: plan.Duration,
    descriptors: plan.Descriptors ?? [],
    offeringId: plan.OfferingId,
    planCode: plan.PlanCode,
    marketCode: plan.MarketCode,
    badge: plan.Badge ?? undefined,
    tagline: plan.Tagline ?? undefined,
    isFeatured: plan.IsFeatured ?? undefined
});

/**
 * Fetch available subscription offerings for the subscribe page.
 */
export const fetchSubscriptionPlans = async (marketCode?: string): Promise<SubscriptionPlan[]> => {
    const response = await axios.get<SubscriptionPlanApi[]>(
        `${BASE_URL}/v1/payment/subscription-offerings`,
        marketCode ? { params: { market: marketCode } } : undefined
    );
    return (response.data ?? []).map(normalizeSubscriptionPlan);
};

/**
 * Fetch published pricing markets and the optional trusted infrastructure detection result.
 */
export const fetchSubscriptionMarkets = async (): Promise<SubscriptionMarketCatalog> => {
    const response = await axios.get<SubscriptionMarketCatalogApi>(`${BASE_URL}/v1/payment/subscription-markets`);
    const data = response.data;
    return {
        defaultMarketCode: data?.DefaultMarketCode ?? '',
        detectedMarketCode: data?.DetectedMarketCode ?? undefined,
        markets: (data?.Markets ?? []).map((market) => ({
            code: market.Code,
            displayName: market.DisplayName,
            currency: market.Currency?.toLowerCase(),
            minorUnitDigits: Number(market.MinorUnitDigits),
            locale: market.Locale,
            countryCodes: market.CountryCodes ?? [],
            timeZones: market.TimeZones ?? []
        }))
    };
};

/**
 * Create a subscription and return the backend response with Stripe intent details.
 * A marketing event id can be supplied for consent-aware server-side conversion dedupe.
 */
export const createSubscription = async (
    offeringId: string,
    checkoutAttemptId: string,
    token: string,
    marketingEventId?: string,
    context?: {
        entryPoint?: string;
        paymentMethodType?: 'apple_pay' | 'google_pay' | 'card';
    }
): Promise<SubscriptionCheckoutResponse> => {
    const response = await axios.post<SubscriptionCheckoutResponse>(`${BASE_URL}/v1/payment/create-subscription`, {
        OfferingId: offeringId,
        CheckoutAttemptId: checkoutAttemptId,
        ...(context?.entryPoint ? { EntryPoint: context.entryPoint } : {}),
        ...(context?.paymentMethodType ? { PaymentMethodType: context.paymentMethodType } : {})
    }, {
        headers: {
            'Authorization': `Bearer ${token}`,
            ...buildMarketingAttributionHeaders(marketingEventId)
        },
    });

    const data = response.data as SubscriptionCheckoutResponse & {
        ClientSecret?: string;
        Type?: 'payment' | 'setup';
        CheckoutAttemptId?: string;
    };
    return {
        clientSecret: data.clientSecret ?? data.ClientSecret ?? '',
        type: data.type ?? data.Type ?? 'payment',
        checkoutAttemptId: data.checkoutAttemptId ?? data.CheckoutAttemptId
    };
};

/**
 * Force-refresh subscription state after Stripe confirms a client-side payment.
 * A marketing event id can be supplied for consent-aware server/browser conversion dedupe.
 */
export const completeSubscription = async (token: string, marketingEventId?: string): Promise<Subscription> => {
    const response = await axios.post<Subscription>(`${BASE_URL}/v1/payment/complete-subscription`, {}, {
        headers: {
            'Authorization': `Bearer ${token}`,
            ...buildMarketingAttributionHeaders(marketingEventId)
        },
    });

    return response.data;
};

/**
 * Cancel the active subscription for the current user.
 */
export const cancelSubscription = async (token: string) => {
    const response = await axios.post(`${BASE_URL}/v1/payment/cancel-subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` }
    });

    return response.data;
};

export type CustomerPortalAction =
    | 'portal'
    | 'payment_method_update'
    | 'invoice_history'
    | 'subscription_management';

/**
 * Create a Stripe customer portal session for a server-allowlisted billing action.
 */
export const createCustomerPortalSession = async (
    token: string,
    action: CustomerPortalAction = 'portal'
): Promise<{ Url: string }> => {
    const response = await axios.post<{ Url: string }>(`${BASE_URL}/v1/payment/customer-portal`, {
        Action: action
    }, {
        headers: { Authorization: `Bearer ${token}` }
    });

    return response.data;
};
